import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, useCurrency, API } from "../App";
import { useTheme } from "../hooks/useTheme";
import BottomNav from "./BottomNav";
import FloatingCart from "./FloatingCart";
import { Wallet, Bell, Store, Package, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const logoSrc = (url) => url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "/logo-black.jpg";

export default function UserLayout({ children, showHeader = true, showNav = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { symbol, logoUrl, homeLogoUrl } = useCurrency();
  const { theme } = useTheme();
  const activeLogoUrl = theme === "dark" ? (homeLogoUrl || logoUrl) : logoUrl;
  const [unreadCount, setUnreadCount] = useState(0);
  const [pickupReady, setPickupReady] = useState(null); // { order_id, order_number }
  const wsRef = useRef(null);
  const statusWsRef = useRef(null);
  const audioCtxRef = useRef(null);

  // Initialize audio context on first user interaction
  useEffect(() => {
    const initAudio = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
    };
    document.addEventListener("click", initAudio, { once: true });
    document.addEventListener("touchstart", initAudio, { once: true });
    return () => {
      document.removeEventListener("click", initAudio);
      document.removeEventListener("touchstart", initAudio);
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Request browser notification permission
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
      const wsUrl = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");

      // Global WebSocket for notifications
      wsRef.current = new WebSocket(`${wsUrl}/ws/notif_${user.user_id}`);
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);

        // Pickup-ready popup: fires when the order is marked for pickup collection
        if (
          data.type === "order_status_update" &&
          data.order_type === "pickup" &&
          data.status === "pick_up"
        ) {
          setPickupReady({ order_id: data.order_id, order_number: data.order_number });
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Collect From Kitchen", {
              body: `Order ${data.order_number || ""} is ready — please collect it from the kitchen.`,
              icon: "/favicon.ico",
            });
          }
        }

        if (data.type === "notification") {
          setUnreadCount((prev) => prev + 1);
          toast.info(data.notification?.title || "New notification");
          // Play notification sound
          try {
            const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
            if (ctx.state === "suspended") ctx.resume();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.setValueAtTime(880, ctx.currentTime);
            osc.frequency.setValueAtTime(660, ctx.currentTime + 0.15);
            osc.type = "sine";
            gain.gain.setValueAtTime(0.4, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.4);
          } catch (e) { console.log("Sound error:", e); }
          // Browser push notification
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Super Morsel", {
              body: data.notification?.title || "You have a new update",
              icon: "/favicon.ico",
            });
          }
        }
      };
      wsRef.current.onerror = () => {};

      // Order status WebSocket: show popup when pickup order is ready or at pick_up step
      statusWsRef.current = new WebSocket(`${wsUrl}/ws/${user.user_id}`);
      statusWsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (
            data.type === "order_status_update" &&
            data.order_type === "pickup" &&
            data.status === "pick_up"
          ) {
            setPickupReady({ order_id: data.order_id, order_number: data.order_number });
            if ("Notification" in window && Notification.permission === "granted") {
              new Notification("Collect From Kitchen", {
                body: `Order ${data.order_number || ""} is ready — please collect it from the kitchen.`,
                icon: "/favicon.ico",
              });
            }
          }
        } catch {}
      };
      statusWsRef.current.onerror = () => {};

      return () => {
        if (wsRef.current) wsRef.current.close();
        if (statusWsRef.current) statusWsRef.current.close();
      };
    }
  }, [user]);

  // Polling fallback: check pickup orders every 15s. Guarantees the popup fires
  // even if WebSocket is blocked/dropped (e.g. Capacitor WebView, ingress issues).
  useEffect(() => {
    if (!user) return;

    const ALERTED_KEY = "sm_pickup_alerted_ids";
    const getAlerted = () => {
      try { return JSON.parse(localStorage.getItem(ALERTED_KEY) || "[]"); }
      catch { return []; }
    };
    const markAlerted = (id) => {
      const set = new Set(getAlerted());
      set.add(id);
      // Keep bounded to last 50 entries
      const arr = Array.from(set).slice(-50);
      localStorage.setItem(ALERTED_KEY, JSON.stringify(arr));
    };

    const checkPickupOrders = async () => {
      try {
        const res = await fetch(`${API}/orders`, { credentials: "include" });
        if (!res.ok) return;
        const orders = await res.json();
        const alerted = new Set(getAlerted());
        const readyPickup = orders.find(
          (o) =>
            o.order_type === "pickup" &&
            o.status === "pick_up" &&
            !alerted.has(o.order_id)
        );
        if (readyPickup) {
          markAlerted(readyPickup.order_id);
          setPickupReady({ order_id: readyPickup.order_id, order_number: readyPickup.order_number });
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Collect From Kitchen", {
              body: `Order ${readyPickup.order_number || ""} is ready — please collect it from the kitchen.`,
              icon: "/favicon.ico",
            });
          }
        }
      } catch {}
    };

    // Run immediately, then every 15 seconds
    checkPickupOrders();
    const id = setInterval(checkPickupOrders, 15000);

    // Also re-check when the app returns to foreground (mobile WebView)
    const onVisible = () => { if (document.visibilityState === "visible") checkPickupOrders(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API}/notifications/unread-count`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="app-container bg-white dark:bg-gray-900 min-h-screen">
      {showHeader && (
        <header className="sticky top-0 z-30 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img src={logoSrc(activeLogoUrl)} alt="Super Morsel" className="w-[52px] h-[52px] object-contain" />
              <div>
                <p className="text-sm font-bold text-gray-900 dark:text-white">{user?.name || "Welcome"}</p>
                <p className="text-[9px] text-gray-200 dark:text-gray-500 font-medium">Super Morsel</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {sessionStorage.getItem("sm_pickup_mode") === "true" && (
                <button
                  onClick={() => { sessionStorage.removeItem("sm_pickup_mode"); navigate("/menu"); }}
                  className="p-1.5 bg-orange-100 rounded-full"
                  title="Pickup Mode Active — tap to exit"
                  data-testid="pickup-mode-indicator"
                >
                  <Store size={18} className="text-orange-600" />
                </button>
              )}
              <button
                onClick={() => navigate("/notifications")}
                className="relative p-1.5"
                data-testid="notification-bell"
              >
                <Bell size={20} className="text-gray-600 dark:text-gray-400" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full" data-testid="notification-badge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-1.5 bg-green-700 text-white px-3 py-1.5 rounded-full">
                <Wallet size={14} />
                <span className="font-bold text-xs" data-testid="header-balance">
                  {symbol}{Math.round(user?.wallet_balance || 0)}
                </span>
              </div>
            </div>
          </div>
        </header>
      )}
      <main className={showNav ? "main-content" : ""}>{children}</main>
      {showNav && <FloatingCart />}
      {showNav && <BottomNav />}

      {/* Pickup Ready Popup - global */}
      {pickupReady && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setPickupReady(null)}
          data-testid="pickup-ready-popup"
        >
          <div
            className="relative bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPickupReady(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              data-testid="close-pickup-ready-popup"
            >
              <X size={18} />
            </button>
            <div className="w-16 h-16 mx-auto mb-3 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
              <Package size={32} className="text-orange-600" />
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 dark:text-white">Collect From Kitchen</h3>
            <p className="text-sm text-gray-500 mt-1">
              {pickupReady.order_number ? `Order ${pickupReady.order_number}` : "Your pickup order"} is ready — please collect it from the kitchen.
            </p>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setPickupReady(null)}
                className="flex-1 py-2.5 text-sm font-semibold rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                data-testid="pickup-ready-dismiss"
              >
                Dismiss
              </button>
              <button
                onClick={() => { const id = pickupReady.order_id; setPickupReady(null); navigate(`/orders/${id}`); }}
                className="flex-1 py-2.5 text-sm font-bold rounded-xl bg-green-600 hover:bg-green-700 text-white"
                data-testid="pickup-ready-track"
              >
                View Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
